import { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { IconChevronDown, IconChevronUp, IconPencil, IconNotes } from "@tabler/icons-react";
import { useUpdateBuildNotes } from "@/api/hooks";
import type { BuildId } from "@/types/identifiers";

interface BuildNotesSectionProps {
  buildId: BuildId;
  notes: string | null | undefined;
  canEdit: boolean;
  readOnly: boolean;
}

export function BuildNotesSection({ buildId, notes, canEdit, readOnly }: BuildNotesSectionProps) {
  const hasNotes = !!notes;
  const [userToggled, setUserToggled] = useState(false);
  const [expanded, setExpanded] = useState(hasNotes);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(notes ?? "");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const updateNotes = useUpdateBuildNotes(buildId);

  // Derive expanded from notes when user hasn't manually toggled
  const effectiveExpanded = userToggled ? expanded : hasNotes;

  function handleToggle() {
    setUserToggled(true);
    setExpanded(!effectiveExpanded);
  }

  function startEditing() {
    setDraft(notes ?? "");
    setIsEditing(true);
  }

  // Focus textarea when entering edit mode
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(
        textareaRef.current.value.length,
        textareaRef.current.value.length,
      );
    }
  }, [isEditing]);

  const handleSave = useCallback(() => {
    if (updateNotes.isPending) return;

    const trimmed = draft.trim();
    const value = trimmed || null;

    if (value === (notes ?? null)) {
      setIsEditing(false);
      return;
    }

    updateNotes.mutate(value, {
      onSuccess: () => {
        toast.success(value ? "Notes updated" : "Notes cleared");
        setIsEditing(false);
      },
      onError: (err) => {
        toast.error(err.message);
      },
    });
  }, [draft, notes, updateNotes]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        handleSave();
      } else if (e.key === "Escape") {
        setDraft(notes ?? "");
        setIsEditing(false);
      }
    },
    [handleSave, notes],
  );

  const editable = canEdit && !readOnly;

  return (
    <div className="mb-6">
      <button
        onClick={handleToggle}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <IconNotes className="h-4 w-4" />
        <span className="font-medium">Notes</span>
        {effectiveExpanded ? (
          <IconChevronUp className="h-3.5 w-3.5" />
        ) : (
          <IconChevronDown className="h-3.5 w-3.5" />
        )}
      </button>

      {effectiveExpanded && (
        <div className="mt-2">
          {isEditing ? (
            <div>
              <textarea
                ref={textareaRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={handleSave}
                onKeyDown={handleKeyDown}
                rows={3}
                disabled={updateNotes.isPending}
                placeholder="Add notes about this build..."
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 resize-y"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Ctrl+Enter to save, Escape to cancel
              </p>
            </div>
          ) : (
            <div
              role={editable ? "button" : undefined}
              tabIndex={editable ? 0 : undefined}
              onClick={editable ? startEditing : undefined}
              onKeyDown={
                editable
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") startEditing();
                    }
                  : undefined
              }
              className={`group text-sm whitespace-pre-wrap ${
                editable ? "cursor-pointer rounded-md px-3 py-2 -mx-3 -my-2 hover:bg-muted/50 transition-colors" : ""
              }`}
            >
              {hasNotes ? (
                <span className="flex items-start gap-2">
                  <span className="flex-1">{notes}</span>
                  {editable && (
                    <IconPencil className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  )}
                </span>
              ) : editable ? (
                <span className="text-muted-foreground italic">Add notes...</span>
              ) : null}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
